import { Wallet } from "lucide-react";
import { useMemo } from "react";

import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { PocketOption } from "./types";

// Above this count the picker switches from a 2-col grid to a search list.
const POCKETS_SEARCH_THRESHOLD = 6;

type PocketPickerFieldProps = {
  pockets: PocketOption[];
  isLoading: boolean;
  pocketId: string | null;
  onSelect: (pocketId: string | null) => void;
  query: string;
  onQueryChange: (query: string) => void;
};

export const PocketPickerField = ({
  pockets,
  isLoading,
  pocketId,
  onSelect,
  query,
  onQueryChange,
}: PocketPickerFieldProps) => {
  const { t } = useTranslations("cards");

  const filteredPockets = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pockets;
    return pockets.filter((p) => p.name.toLowerCase().includes(q));
  }, [pockets, query]);

  const useSearch = pockets.length > POCKETS_SEARCH_THRESHOLD;

  return (
    <section className="space-y-2">
      <label className="text-sm font-medium text-foreground">
        {t("create_card_label_pocket")}{" "}
        <span className="text-xs font-normal text-muted-foreground">({t("create_card_label_optional")})</span>
      </label>

      {isLoading ? (
        <div className="rounded-md border border-border bg-card p-3 text-sm text-muted-foreground">
          {t("identities_loading")}
        </div>
      ) : pockets.length === 0 ? (
        <div className="rounded-md border border-border bg-card p-3 text-sm text-muted-foreground">
          {t("create_card_no_pockets_hint")}
        </div>
      ) : useSearch ? (
        <div className="space-y-2">
          <ColocaSearchInput
            value={query}
            onValueChange={onQueryChange}
            placeholder={t("create_card_search_pocket_placeholder")}
          />
          <div className="max-h-[220px] overflow-y-auto rounded-md border border-border bg-card">
            <button
              type="button"
              onClick={() => onSelect(null)}
              className={cn(
                "flex w-full items-center gap-3 border-b border-border p-2.5 text-left transition-colors last:border-b-0 hover:bg-muted/40",
                pocketId === null && "bg-primary/5",
              )}
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
                <Wallet className="h-3.5 w-3.5" />
              </span>
              <div className="text-sm font-medium text-foreground">{t("create_card_pocket_none")}</div>
            </button>
            {filteredPockets.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelect(p.id)}
                className={cn(
                  "flex w-full items-center gap-3 border-b border-border p-2.5 text-left transition-colors last:border-b-0 hover:bg-muted/40",
                  pocketId === p.id && "bg-primary/5",
                )}
              >
                <span
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md"
                  style={{ background: p.color }}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{p.name}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => onSelect(null)}
            className={cn(
              "flex items-center gap-2.5 rounded-md border p-2.5 text-left transition-all",
              pocketId === null ? "border-primary bg-primary/5" : "border-border hover:border-border/80",
            )}
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <Wallet className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-foreground">{t("create_card_pocket_none")}</div>
              <div className="text-[11px] text-muted-foreground">
                {t("create_card_pocket_none_description")}
              </div>
            </div>
          </button>
          {pockets.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p.id)}
              className={cn(
                "flex items-center gap-2.5 rounded-md border p-2.5 text-left transition-all",
                pocketId === p.id ? "border-primary bg-primary/5" : "border-border hover:border-border/80",
              )}
            >
              <span
                className="inline-flex h-7 w-7 items-center justify-center rounded-md"
                style={{ background: p.color }}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">{p.name}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
};
